import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Nav, Row, Col } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../shared/api';
import { COLOR_PRIMARY, COLOR_SUCCESS, COLOR_WARNING, COLOR_GREEN } from '../../shared/colorUtils';
import { CenteredLoader } from '../../components/LoadingSpinner';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import StatsCard from '../../components/ui/StatsCard';
import StatusBadge from '../../components/ui/StatusBadge';
import EmptyState from '../../components/feedback/EmptyState';

const CrewStatistics = () => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState('overview');

  const { data: statistics, isLoading: loading, isError, refetch } = useQuery({
    queryKey: ['crew', 'statistics'],
    queryFn: async () => {
      const [overview, byProfession, byCompany, userAccounts] = await Promise.all([
        api.get('crew/api/crew-stats/overview/'),
        api.get('crew/api/crew-stats/by_profession/'),
        api.get('crew/api/crew-stats/by_company/'),
        api.get('crew/api/crew-stats/user_accounts/')
      ]);
      return {
        overview,
        byProfession: byProfession || [],
        byCompany: byCompany || [],
        userAccounts
      };
    },
  });
  const error = isError ? t('crew.errors.failedToLoadData') : null;

  const renderProfessionChart = () => {
    if (!statistics?.byProfession || statistics.byProfession.length === 0) {
      return (
        <EmptyState
          title={t('crew.statisticsPage.noProfessionData')}
          message={t('crew.statisticsPage.noProfessionDataMessage')}
        />
      );
    }

    const maxCount = Math.max(...statistics.byProfession.map(item => item.count));

    return (
      <div className="space-y-3">
        {statistics.byProfession.map((item, index) => (
          <div key={index} className="d-flex align-items-center">
            <div className="me-3 text-muted" style={{ minWidth: '120px' }}>
              {item.profession__name || t('crew.unknownProfession')}
            </div>
            <div className="flex-fill">
              <div className="progress" style={{ height: '8px' }}>
                <div
                  className="progress-bar bg-primary"
                  role="progressbar"
                  style={{ width: `${(item.count / maxCount) * 100}%` }}
                  aria-valuenow={item.count}
                  aria-valuemin="0"
                  aria-valuemax={maxCount}
                ></div>
              </div>
            </div>
            <div className="ms-3 text-muted">
              <StatusBadge color={COLOR_PRIMARY} label={String(item.count)} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCompanyChart = () => {
    if (!statistics?.byCompany || statistics.byCompany.length === 0) {
      return (
        <EmptyState
          title={t('crew.statisticsPage.noCompanyData')}
          message={t('crew.statisticsPage.noCompanyDataMessage')}
        />
      );
    }

    const maxCount = Math.max(...statistics.byCompany.map(item => item.count));

    return (
      <div className="space-y-3">
        {statistics.byCompany.map((item, index) => (
          <div key={index} className="d-flex align-items-center">
            <div className="me-3 text-muted" style={{ minWidth: '150px' }}>
              {item.company__company_name || t('crew.noCompany')}
            </div>
            <div className="flex-fill">
              <div className="progress" style={{ height: '8px' }}>
                <div
                  className="progress-bar bg-green"
                  role="progressbar"
                  style={{ width: `${(item.count / maxCount) * 100}%` }}
                  aria-valuenow={item.count}
                  aria-valuemin="0"
                  aria-valuemax={maxCount}
                ></div>
              </div>
            </div>
            <div className="ms-3 text-muted">
              <StatusBadge color={COLOR_GREEN} label={String(item.count)} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <PageShell pretitle={t('crew.title')} title={t('crew.statisticsPage.title')}>
        <PageSection>
          <CenteredLoader message={t('crew.loading')} />
        </PageSection>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell pretitle={t('crew.title')} title={t('crew.statisticsPage.title')}>
        <PageSection>
          <Alert variant="danger" className="app-inline-alert mb-0">{error}</Alert>
        </PageSection>
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('crew.title')}
      title={t('crew.statisticsPage.title')}
      description={t('crew.statisticsPage.subtitle')}
      actions={(
        <button
          className="btn btn-outline-primary"
          onClick={refetch}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4"/>
            <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4"/>
          </svg>
          {t('common.refresh')}
        </button>
      )}
    >
      <PageSection title={t('crew.statisticsPage.title')} description={t('crew.statisticsPage.sectionDescription')}>
        <div className="app-stat-grid">
          <StatsCard title={t('crew.statisticsPage.totalMembers')} value={statistics?.overview?.total_crew || 0} icon="users" colorClass="primary" />
          <StatsCard title={t('crew.statisticsPage.withUserAccounts')} value={statistics?.overview?.crew_with_accounts || 0} icon="user-check" colorClass="success" />
          <StatsCard title={t('crew.statisticsPage.withoutUserAccounts')} value={statistics?.overview?.crew_without_accounts || 0} icon="user-x" colorClass="warning" />
          <StatsCard title={t('crew.statisticsPage.activeMembers')} value={statistics?.overview?.active_crew_last_30_days || 0} icon="activity" colorClass="info" />
        </div>
      </PageSection>

      <div className="card app-surface">
        <div className="card-body p-3">
          <Nav className="app-tabnav">
            <Nav.Item>
              <Nav.Link as="button" type="button" active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
                {t('crew.statisticsPage.tabs.overview')}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link as="button" type="button" active={activeTab === 'professions'} onClick={() => setActiveTab('professions')}>
                {t('crew.statisticsPage.tabs.byProfession')}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link as="button" type="button" active={activeTab === 'companies'} onClick={() => setActiveTab('companies')}>
                {t('crew.statisticsPage.tabs.byCompany')}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <div className="pt-3">
            {activeTab === 'overview' && (
              <Row className="g-3">
                <Col md={6}>
                  <PageSection title={t('crew.statisticsPage.userAccountStatistics')}>
                    <div className="datagrid">
                      <div className="datagrid-item">
                        <div className="datagrid-title">{t('crew.statisticsPage.totalMembers')}</div>
                        <div className="datagrid-content"><strong>{statistics?.userAccounts?.total_crew || 0}</strong></div>
                      </div>
                      <div className="datagrid-item">
                        <div className="datagrid-title">{t('crew.statisticsPage.withUserAccounts')}</div>
                        <div className="datagrid-content">
                          <StatusBadge className="me-2" color={COLOR_SUCCESS} label={String(statistics?.userAccounts?.with_accounts || 0)} />
                          <small className="text-muted">({statistics?.userAccounts?.percentage_with_accounts ? `${Math.round(statistics.userAccounts.percentage_with_accounts)}%` : '0%'})</small>
                        </div>
                      </div>
                      <div className="datagrid-item">
                        <div className="datagrid-title">{t('crew.statisticsPage.withoutUserAccounts')}</div>
                        <div className="datagrid-content">
                          <StatusBadge color={COLOR_WARNING} label={String(statistics?.userAccounts?.without_accounts || 0)} />
                        </div>
                      </div>
                    </div>
                  </PageSection>
                </Col>
                <Col md={6}>
                  <PageSection title={t('crew.statisticsPage.activityStatistics')}>
                    <div className="datagrid">
                      <div className="datagrid-item">
                        <div className="datagrid-title">{t('crew.statisticsPage.activeMembers')}</div>
                        <div className="datagrid-content">
                          <StatusBadge color={COLOR_PRIMARY} label={String(statistics?.overview?.active_crew_last_30_days || 0)} />
                          <small className="text-muted ms-2">{t('crew.statisticsPage.activeWindow')}</small>
                        </div>
                      </div>
                      <div className="datagrid-item">
                        <div className="datagrid-title">{t('crew.statisticsPage.totalProfessions')}</div>
                        <div className="datagrid-content">{statistics?.byProfession?.length || 0}</div>
                      </div>
                      <div className="datagrid-item">
                        <div className="datagrid-title">{t('crew.statisticsPage.totalCompanies')}</div>
                        <div className="datagrid-content">{statistics?.byCompany?.length || 0}</div>
                      </div>
                    </div>
                  </PageSection>
                </Col>
              </Row>
            )}

            {activeTab === 'professions' && (
              <PageSection title={t('crew.statisticsPage.membersByProfession')} actions={<StatusBadge color={COLOR_PRIMARY} label={`${statistics?.byProfession?.length || 0} ${t('crew.statisticsPage.labels.professions')}`} />}>
                {renderProfessionChart()}
              </PageSection>
            )}

            {activeTab === 'companies' && (
              <PageSection title={t('crew.statisticsPage.membersByCompany')} actions={<StatusBadge color={COLOR_GREEN} label={`${statistics?.byCompany?.length || 0} ${t('crew.statisticsPage.labels.companies')}`} />}>
                {renderCompanyChart()}
              </PageSection>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default CrewStatistics;
